import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import VerifiedIcon from "@mui/icons-material/VerifiedUserOutlined";
import NewIcon from "@mui/icons-material/PersonAddAltOutlined";
import ActiveIcon from "@mui/icons-material/BoltOutlined";
import {
  PORTAL_CUSTOMER_PAGE_SIZE,
  type PortalCustomerKind,
  type PortalCustomerList,
  type PortalCustomerQuery,
  type PortalCustomerSort,
  type PortalCustomerStatus,
} from "@shared/portal-customers";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { KpiCard } from "../components/KpiCard";
import PortalCustomerDrawer, {
  StatusChip,
  moneyByCurrency,
} from "../components/portal/PortalCustomerDrawer";
import { tokens } from "../theme";
import { friendlyError } from "../utils/apiError";
import { portalCustomersApi } from "../http/portalCustomers";
import { useFormat } from "../i18n/format";

/**
 * Portal customers - the Nenda staff register of everyone registered on a
 * consumer TVOD purchase portal (Distribution → Portal customers).
 *
 * Sigge ruled 2026-10-04 that Nenda admins see these customers: Nenda is the
 * data controller, so this is legitimate staff access, and every access is
 * audited. The server records each list query (its filters), each customer
 * opened and each action with the reason typed. Filtering, sorting and
 * paging all happen on the server; this page only holds the query.
 *
 * `?portal=<ownerId>` preselects a portal - the purchase portal dialog links
 * here that way.
 */

interface Filters {
  /** An owner id, or `all`. */
  portal: string;
  confirmed: "" | "true" | "false";
  kind: "" | PortalCustomerKind;
  status: "" | PortalCustomerStatus;
  /** `YYYY-MM-DD`, or "" for no bound. */
  createdFrom: string;
  createdTo: string;
}

const NO_FILTERS: Filters = {
  portal: "all",
  confirmed: "",
  kind: "",
  status: "",
  createdFrom: "",
  createdTo: "",
};

/** The 0-10 rule: more options than this get a search field. */
const SEARCHABLE_OVER = 10;

const KIND_KEY = {
  private: "portalCustomersPage.kind.private",
  member: "portalCustomersPage.kind.member",
} as const satisfies Record<PortalCustomerKind, string>;

const EMPTY = "—";

function initialPortal(): string {
  try {
    return new URLSearchParams(window.location.search).get("portal") || "all";
  } catch {
    return "all";
  }
}

export default function PortalCustomersPage() {
  const [filters, setFilters] = useState<Filters>(() => ({
    ...NO_FILTERS,
    portal: initialPortal(),
  }));
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<PortalCustomerSort>("created");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PORTAL_CUSTOMER_PAGE_SIZE);
  const [data, setData] = useState<PortalCustomerList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const day = (iso: string | null) => (iso ? fmt.date(iso) : EMPTY);

  // Search as you type, but one request per pause - each one is audited.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useMemo<PortalCustomerQuery>(
    () => ({
      portal: filters.portal,
      confirmed: filters.confirmed || undefined,
      kind: filters.kind || undefined,
      status: filters.status || undefined,
      createdFrom: filters.createdFrom || undefined,
      createdTo: filters.createdTo || undefined,
      q: q || undefined,
      sort,
      dir,
      page,
      pageSize,
    }),
    [filters, q, sort, dir, page, pageSize]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    portalCustomersApi.list(query).then(
      (res) => {
        if (!alive) return;
        setData(res);
        setError(null);
        setLoading(false);
      },
      (err) => {
        if (!alive) return;
        setError(friendlyError(t, err));
        setLoading(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [query, reload, t]);

  const setFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) => {
      setFilters((f) => ({ ...f, [key]: value }));
      setPage(1);
    },
    []
  );

  const filtered =
    q !== "" ||
    Object.entries(filters).some(([k, v]) =>
      k === "portal" ? v !== "all" : v !== ""
    );

  const sortBy = (key: PortalCustomerSort) => {
    if (sort === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(key);
      setDir(key === "email" ? "asc" : "desc");
    }
    setPage(1);
  };

  const head = (key: PortalCustomerSort, label: string, align?: "right") => (
    <TableCell sortDirection={sort === key ? dir : false} align={align}>
      <TableSortLabel
        active={sort === key}
        direction={sort === key ? dir : key === "email" ? "asc" : "desc"}
        onClick={() => sortBy(key)}
        data-testid={`customers-sort-${key}`}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const portals = data?.portals ?? [];
  const totals = data?.totals;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("portalCustomersPage.eyebrow")}
        title={t("portalCustomersPage.title")}
        subtitle={t("portalCustomersPage.subtitle")}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          mb: 3,
        }}
        data-testid="customers-totals"
      >
        <KpiCard
          label={t("portalCustomersPage.kpi.customers")}
          value={totals ? String(totals.customers) : EMPTY}
          icon={<PeopleIcon />}
        />
        <KpiCard
          label={t("portalCustomersPage.kpi.confirmedEmail")}
          value={totals ? String(totals.confirmed) : EMPTY}
          icon={<VerifiedIcon />}
        />
        <KpiCard
          label={t("portalCustomersPage.kpi.newThisMonth")}
          value={totals ? String(totals.newThisMonth) : EMPTY}
          icon={<NewIcon />}
        />
        <KpiCard
          label={t("portalCustomersPage.kpi.active30d")}
          value={totals ? String(totals.active30d) : EMPTY}
          sub={t("portalCustomersPage.kpi.active30dSub")}
          icon={<ActiveIcon />}
        />
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("portalCustomersPage.searchPlaceholder")}
          sx={{ flex: "1 1 240px", maxWidth: 340 }}
          inputProps={{ "data-testid": "customers-search" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {portals.length > SEARCHABLE_OVER ? (
          <Autocomplete
            size="small"
            sx={{ width: 220 }}
            options={portals}
            getOptionLabel={(o) => o.name}
            value={portals.find((p) => p.ownerId === filters.portal) ?? null}
            onChange={(_, v) => setFilter("portal", v?.ownerId ?? "all")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("common:labels.portal")}
                placeholder={t("portalCustomersPage.allPortals")}
              />
            )}
          />
        ) : (
          <TextField
            select
            size="small"
            label={t("common:labels.portal")}
            value={filters.portal}
            onChange={(e) => setFilter("portal", e.target.value)}
            sx={{ minWidth: 180 }}
            inputProps={{ "data-testid": "customers-filter-portal" }}
          >
            <MenuItem value="all">
              {t("portalCustomersPage.allPortals")}
            </MenuItem>
            {portals.map((p) => (
              <MenuItem key={p.ownerId} value={p.ownerId}>
                {p.name}
              </MenuItem>
            ))}
            {/* A preselected portal shows its id until the list arrives. */}
            {filters.portal !== "all" &&
            !portals.some((p) => p.ownerId === filters.portal) ? (
              <MenuItem value={filters.portal}>{filters.portal}</MenuItem>
            ) : null}
          </TextField>
        )}
        <FilterSelect
          label={t("portalCustomersPage.filters.email")}
          value={filters.confirmed}
          onChange={(v) => setFilter("confirmed", v as Filters["confirmed"])}
          testId="customers-filter-confirmed"
          options={[
            ["true", t("portalCustomersPage.confirmed")],
            ["false", t("portalCustomersPage.notConfirmed")],
          ]}
        />
        <FilterSelect
          label={t("portalCustomersPage.filters.type")}
          value={filters.kind}
          onChange={(v) => setFilter("kind", v as Filters["kind"])}
          testId="customers-filter-kind"
          options={[
            ["private", t(KIND_KEY.private)],
            ["member", t(KIND_KEY.member)],
          ]}
        />
        <FilterSelect
          label={t("portalCustomersPage.filters.status")}
          value={filters.status}
          onChange={(v) => setFilter("status", v as Filters["status"])}
          testId="customers-filter-status"
          options={[
            ["active", t("portalCustomersPage.status.active")],
            ["locked", t("portalCustomersPage.status.locked")],
            ["redacted", t("portalCustomersPage.status.redacted")],
          ]}
        />
        <TextField
          size="small"
          type="date"
          label={t("portalCustomersPage.filters.registeredFrom")}
          value={filters.createdFrom}
          onChange={(e) => setFilter("createdFrom", e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "customers-filter-from" }}
          sx={{ width: 165 }}
        />
        <TextField
          size="small"
          type="date"
          label={t("portalCustomersPage.filters.registeredTo")}
          value={filters.createdTo}
          onChange={(e) => setFilter("createdTo", e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "customers-filter-to" }}
          sx={{ width: 165 }}
        />
        {filtered ? (
          <Button
            size="small"
            onClick={() => {
              setFilters(NO_FILTERS);
              setSearch("");
              setQ("");
              setPage(1);
            }}
            data-testid="customers-clear"
          >
            {t("portalCustomersPage.showAllCustomers")}
          </Button>
        ) : null}
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          bgcolor: tokens.surface,
          border: "1px solid",
          borderColor: tokens.divider,
          borderRadius: "14px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <TableContainer>
          <Table size="small" data-testid="customers-table">
            <TableHead>
              <TableRow>
                {head("email", t("portalCustomersPage.columns.customer"))}
                <TableCell>{t("portalCustomersPage.columns.portal")}</TableCell>
                <TableCell>{t("portalCustomersPage.columns.type")}</TableCell>
                <TableCell>{t("portalCustomersPage.columns.email")}</TableCell>
                <TableCell>{t("portalCustomersPage.columns.status")}</TableCell>
                {head("created", t("portalCustomersPage.columns.registered"))}
                {head("lastLogin", t("portalCustomersPage.columns.lastSignIn"))}
                <TableCell align="right">
                  {t("portalCustomersPage.columns.rentals")}
                </TableCell>
                {head("paid", t("portalCustomersPage.columns.paid"), "right")}
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                      data-testid="customers-empty"
                    >
                      {filtered
                        ? t("portalCustomersPage.emptyFiltered")
                        : t("portalCustomersPage.emptyAll")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {(data?.rows ?? []).map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  onClick={() => setOpen(r.id)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`customer-row-${r.id}`}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ color: tokens.ink }}>
                      {r.email}
                    </Typography>
                    {r.displayName ? (
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.inkMuted }}
                      >
                        {r.displayName}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{r.portal.name}</TableCell>
                  <TableCell>
                    {r.kind === "member"
                      ? r.organisation?.name || t(KIND_KEY.member)
                      : t(KIND_KEY.private)}
                  </TableCell>
                  <TableCell>
                    {r.confirmed
                      ? t("portalCustomersPage.confirmed")
                      : t("portalCustomersPage.notConfirmed")}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={r.status} />
                  </TableCell>
                  <TableCell>{day(r.createdAt)}</TableCell>
                  <TableCell>{day(r.lastLoginAt)}</TableCell>
                  <TableCell align="right">{r.rentals}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {moneyByCurrency(fmt, r.paidTotal)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {loading ? (
          <Stack
            alignItems="center"
            sx={{
              py: data ? 1 : 6,
              position: data ? "absolute" : "static",
              top: 8,
              right: 12,
            }}
          >
            <CircularProgress size={data ? 16 : 26} />
          </Stack>
        ) : null}
        <TablePagination
          component="div"
          count={data?.pagination.total ?? 0}
          page={Math.max(0, page - 1)}
          onPageChange={(_, p) => setPage(p + 1)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          data-testid="customers-pagination"
        />
      </Box>

      {open ? (
        <PortalCustomerDrawer
          key={open}
          customerId={open}
          onClose={() => setOpen(null)}
          onChanged={() => setReload((n) => n + 1)}
        />
      ) : null}
    </PageContainer>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
  testId: string;
}) {
  const { t } = useTranslation("common");
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        minWidth: 150,
        // Closed-active looks different from closed-inactive.
        ...(value
          ? {
              "& .MuiOutlinedInput-root": { bgcolor: tokens.brandTintSoft },
            }
          : {}),
      }}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ "data-testid": testId }}
    >
      <MenuItem value="">{t("labels.all")}</MenuItem>
      {options.map(([v, l]) => (
        <MenuItem key={v} value={v}>
          {l}
        </MenuItem>
      ))}
    </TextField>
  );
}
